/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Interface, ZeroAddress } from 'ethers'

import AmbireAccount from '../../../contracts/compiled/AmbireAccount.json'
import AmbireFactory from '../../../contracts/compiled/AmbireFactory.json'
import { ENTRY_POINT_MARKER, ERC_4337_ENTRYPOINT } from '../../consts/deploy'
import { IActivityController } from '../../interfaces/activity'
import { Hex } from '../../interfaces/hex'
import { RPCProvider } from '../../interfaces/provider'
import { AccountOp, getSignableCalls } from '../accountOp/accountOp'
import { BROADCAST_OPTIONS } from '../broadcast/broadcast'
import {
  BundlerStateOverride,
  FeePaymentOption,
  FullEstimation,
  FullEstimationSummary
} from '../estimate/interfaces'
import { getBroadcastGas } from '../gasPrice/gasPrice'
import { TokenResult } from '../portfolio'
import { isNative } from '../portfolio/helpers'
import { privSlot } from '../proxyDeploy/deploy'
import { UserOperation } from '../userOperation/types'
import { getSpoof } from './account'
import { BaseAccount } from './BaseAccount'
import { isTransferredTokenFeeOption } from './feeOptions'

// this class describes a plain EOA that cannot transition
// to 7702 either because the network or the hardware wallet doesnt' support it
export class V2 extends BaseAccount {
  // we're state overriding the estimation to make it think
  // the account is deployed and it has the entry point as a signer
  //
  // deployment costs are already added and calculated by the ambire estimation
  // we're adding 20k gas for SSTORE in the privilege for the entry point
  // and 15k gas entry point overhead to be on the safe side
  ENTRY_POINT_DEPLOYMENT_ADDITIONAL_GAS = 35000n

  #isTransitioningTo4337() {
    return this.accountState.isDeployed && !this.accountState.isErc4337Enabled
  }

  /**
   * ZKX: can this network broadcast a 4337 userOp at all? Upstream assumes every
   * relayer-enabled chain also has a bundler, which does not hold here:
   * PulseChain is advertised with `hasRelayer: true` by the backend while its
   * `erc4337` block is fully disabled. Routing a userOp to a non-existent
   * bundler there produced the "0 <native> / Insufficient amount" fee row.
   */
  #canUseBundler() {
    return !!this.network.erc4337?.enabled && !!this.network.erc4337?.hasBundlerSupport
  }

  getEstimationCriticalError(estimation: FullEstimation): Error | null {
    if (estimation.ambire instanceof Error) return estimation.ambire
    return null
  }

  supportsBundlerEstimation() {
    return !this.#isTransitioningTo4337()
  }

  getAvailableFeeOptions(
    estimation: FullEstimationSummary,
    feePaymentOptions: FeePaymentOption[],
    op: AccountOp
  ): FeePaymentOption[] {
    const hasPaymaster =
      estimation.bundlerEstimation && estimation.bundlerEstimation.paymaster.isUsable()

    // ZKX: a v2 account can broadcast an op paid by itself only through the
    // relayer or a 4337 bundler. On chains with neither (e.g. PulseChain until
    // the ZKX relayer goes live) every paidBy-self option is dead weight: it
    // can never be quoted (#updateFeeSpeeds bails without a bundler estimation)
    // and rendered as a misleading "0 <native> / Insufficient amount" row, so
    // drop them and leave only the EOA-pays-native options.
    const canBroadcastSelf = this.network.hasRelayer || !!estimation.bundlerEstimation

    return feePaymentOptions.filter(
      (opt) =>
        // always show account native, even if not enough
        (isNative(opt.token) && opt.paidBy === this.account.addr && canBroadcastSelf) ||
        // show EOA native only if it has amount to pay the fee
        (isNative(opt.token) && opt.availableAmount > 0n && opt.paidBy !== this.account.addr) ||
        ((opt.availableAmount > 0n || isTransferredTokenFeeOption(opt, op)) &&
          (this.network.hasRelayer || hasPaymaster))
    )
  }

  getGasUsed(
    estimation: FullEstimationSummary | Error,
    options: {
      feeToken: TokenResult
      op: AccountOp
    }
  ): bigint {
    const isError = estimation instanceof Error
    if (isError || !estimation.ambireEstimation) return 0n

    const ambireBroaddcastGas = getBroadcastGas(this, options.op)
    const ambireGas = ambireBroaddcastGas + estimation.ambireEstimation.gasUsed

    // are we transitioning to 4337?
    if (this.#isTransitioningTo4337()) return ambireGas

    // use the bundler if it doesn't have an error
    if (!estimation.bundlerEstimation) return ambireGas
    let bundlerGasUsed = BigInt(estimation.bundlerEstimation.callGasLimit)

    // if the account is not deployed, add the ambire estimation deployment calc
    // to the bundler total as we're state overriding the bundler to think
    // the account is already deployed during estimation
    if (!this.accountState.isDeployed)
      bundlerGasUsed +=
        estimation.ambireEstimation.deploymentGas + this.ENTRY_POINT_DEPLOYMENT_ADDITIONAL_GAS

    return bundlerGasUsed > ambireGas ? bundlerGasUsed : ambireGas
  }

  getBroadcastOption(
    feeOption: FeePaymentOption,
    options: {
      op: AccountOp
    }
  ): string {
    if (feeOption.paidBy !== this.getAccount().addr) return BROADCAST_OPTIONS.byOtherEOA

    // keep the relayer only for the transition transaction
    // when the account is deployed but it doesn't have the entry point
    // as a signer
    if (this.#isTransitioningTo4337()) return BROADCAST_OPTIONS.byRelayer

    // ZKX: on a relayer chain without 4337 the bundler route is a dead end —
    // `#updateFeeSpeeds` bails out of its byBundler branch when there is no
    // bundler estimation, leaving the option unquoted and rendered as
    // "0 <native> / Insufficient amount" even though the account is funded.
    if (!this.#canUseBundler() && this.network.hasRelayer) return BROADCAST_OPTIONS.byRelayer

    return BROADCAST_OPTIONS.byBundler
  }

  shouldIncludeActivatorCall(paidBy?: string) {
    // if the account is not deployed and we're paying with an EOA,
    // include the 4337 priv
    if (
      !this.accountState.isDeployed &&
      paidBy &&
      paidBy.toLowerCase() !== this.account.addr.toLowerCase()
    )
      return true

    return this.#isTransitioningTo4337()
  }

  canUseReceivingNativeForFee(): boolean {
    return true
  }

  getBroadcastCalldata(accountOp: AccountOp): Hex {
    if (this.accountState.isDeployed) {
      const ambireAccount = new Interface(AmbireAccount.abi)
      return ambireAccount.encodeFunctionData('executeBySender', [
        getSignableCalls(accountOp)
      ]) as Hex
    }

    const ambireFactory = new Interface(AmbireFactory.abi)
    return ambireFactory.encodeFunctionData('deployAndExecute', [
      this.account.creation!.bytecode,
      this.account.creation!.salt,
      getSignableCalls(accountOp),
      getSpoof(this.account)
    ]) as Hex
  }

  getBundlerStateOverride(userOp: UserOperation): BundlerStateOverride | undefined {
    if (this.accountState.isDeployed || !!userOp.factory) return undefined

    return {
      [this.account.addr]: {
        code: AmbireAccount.binRuntime,
        stateDiff: {
          [privSlot(0, 'uint256', ERC_4337_ENTRYPOINT, 'uint256')]: ENTRY_POINT_MARKER
        }
      }
    }
  }

  // we need to authorize the entry point as a signer if we're deploying
  // the account via 4337
  shouldSignDeployAuth(broadcastOption: string): boolean {
    return broadcastOption === BROADCAST_OPTIONS.byBundler && !this.accountState.isDeployed
  }

  isSponsorable(): boolean {
    return this.network.chainId === 100n
  }

  getAtomicStatus(): 'unsupported' | 'supported' | 'ready' {
    return 'supported'
  }

  getNonceId(): string {
    // v2 accounts have two nonces: ambire smart account & entry point nonce
    return `${this.accountState.nonce.toString()}-${this.accountState.erc4337Nonce.toString()}`
  }

  /**
   * `op.nonce` is captured once, when the SignAccountOpController is built, and
   * is never refreshed afterwards. If another op for this account confirms while
   * the sign screen is open, that snapshot goes stale - and because the v2
   * signature commits to the nonce (getSignableHash -> [addr, chainId, nonce,
   * calls]), broadcasting it would revert on chain with INSUFFICIENT_PRIVILEGE.
   * So re-read the nonce right before signing.
   *
   * `latest` rather than `pending` is deliberate. SignAccountOp blocks signing
   * while one of our own relayer ops is still in flight, so nothing of ours is
   * unmined here; and a reverted `execute()` rolls the nonce increment back, so
   * the mined state is exactly what the signature has to match.
   */
  async getBroadcastNonce(
    activity: IActivityController,
    op: AccountOp,
    provider: RPCProvider
  ): Promise<bigint> {
    const ambireInterface = new Interface(AmbireAccount.abi)
    const onchainNonce = await provider
      .send('eth_call', [
        { to: op.accountAddr, data: ambireInterface.encodeFunctionData('nonce') },
        'latest'
      ])
      .catch(() => null)

    // '0x' comes back for an account that is not deployed yet; keep the
    // locally built nonce in that case (and whenever the read fails).
    if (!onchainNonce || onchainNonce === '0x') return op.nonce as bigint

    return BigInt(onchainNonce)
  }

  /**
   * The Ambire estimation is made to work perfectly with Ambire SA
   */
  shouldStateOverrideDuringSimulations(): boolean {
    return false
  }

  canBroadcastByOtherEOA(): boolean {
    return true
  }

  canSetCustomGasPrices(feeOption: FeePaymentOption): boolean {
    return (
      feeOption.token.address === ZeroAddress &&
      feeOption.paidBy.toLowerCase() !== this.account.addr.toLowerCase()
    )
  }

  canSetCustomGas(feeOption: FeePaymentOption): boolean {
    return this.canSetCustomGasPrices(feeOption)
  }
}
